import { ChevronDown, Database } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ClientHealth, HealthStatus } from "../types";

export function statusClass(status: HealthStatus | undefined): string {
  if (status === "VERDE") return "is-good";
  if (status === "AMARILLO") return "is-warning";
  if (status === "ROJO") return "is-danger";
  return "is-muted";
}

export function HealthPill({
  label,
  status,
  detail,
}: {
  label: string;
  status?: HealthStatus;
  detail?: string;
}) {
  return (
    <span className={`health-pill ${statusClass(status)}`}>
      <span className="health-dot" />
      <span>{label}</span>
      {detail && <small>{detail}</small>}
    </span>
  );
}

export function HealthCluster({
  health,
  healthState,
  selectedClient,
}: {
  health?: ClientHealth;
  healthState: "idle" | "loading" | "ready" | "error";
  selectedClient: string;
}) {
  if (selectedClient === "ALL") {
    return (
      <div className="health-cluster" aria-label="Contexto de datos de cartera">
        <HealthPill label="CARTERA" status="VERDE" detail="live" />
      </div>
    );
  }
  if (!health) {
    return (
      <div className="health-cluster" aria-live="polite">
        <HealthPill label={healthState === "error" ? "SALUD NO DISPONIBLE" : "CARGANDO SALUD"} />
      </div>
    );
  }
  return (
    <div className="health-cluster" aria-label="Salud de datos del cliente">
      <HealthPill
        label="SEO·GSC"
        status={health.gscStatus}
        detail={health.gscDays === null ? "s/d" : `${health.gscDays}d`}
      />
      <HealthPill
        label="SEO·GA4"
        status={health.ga4Status}
        detail={health.ga4Days === null ? "s/d" : `${health.ga4Days}d`}
      />
      {health.semrushStatus && health.semrushStatus !== "NO_APLICA" && (
        <HealthPill
          label="Semrush"
          status={health.semrushStatus}
          detail={health.semrushDays === null ? "s/d" : `${health.semrushDays}d`}
        />
      )}
      {(health.paidPlatforms && health.paidPlatforms.length > 0) ? (
        health.paidPlatforms.map((p) => (
          <HealthPill
            key={p.platform}
            label={p.platform}
            status={p.status}
            detail={`${p.days ?? "s/d"}d`.trim()}
          />
        ))
      ) : (
        <HealthPill
          label="PAID"
          status={health.paidStatus}
          detail={`${health.paidPlatform ?? ""} ${health.paidDays ?? "s/d"}d`.trim()}
        />
      )}
    </div>
  );
}

const HEALTH_RANK: Record<HealthStatus, number> = { ROJO: 3, AMARILLO: 2, VERDE: 1, NO_APLICA: 0 };

function worstStatus(health: ClientHealth | undefined): HealthStatus {
  if (!health) return "NO_APLICA";
  const statuses: HealthStatus[] = [
    health.gscStatus,
    health.ga4Status,
    ...(health.semrushStatus && health.semrushStatus !== "NO_APLICA" ? [health.semrushStatus] : []),
    ...(health.paidPlatforms?.length ? health.paidPlatforms.map((p) => p.status) : [health.paidStatus]),
  ];
  return statuses.reduce((worst, current) => (HEALTH_RANK[current] > HEALTH_RANK[worst] ? current : worst), "NO_APLICA");
}

/** Las píldoras de frescura (SEO/Paid) viven detrás de este trigger en vez de siempre
 *  visibles: son contexto de "¿puedo confiar en esto?", no la primera línea del header. */
export function HealthPopover({
  health,
  healthState,
  selectedClient,
}: {
  health?: ClientHealth;
  healthState: "idle" | "loading" | "ready" | "error";
  selectedClient: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const worst = selectedClient === "ALL" ? "VERDE" : worstStatus(health);

  return (
    <div className={open ? "health-popover is-open" : "health-popover"} ref={rootRef}>
      <button
        type="button"
        className="health-popover-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Frescura de datos"
        title="Frescura de datos"
        onClick={() => setOpen((value) => !value)}
      >
        <Database size={14} aria-hidden="true" />
        <span className={`health-dot ${statusClass(worst)}`} />
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div className="health-popover-panel">
          <HealthCluster health={health} healthState={healthState} selectedClient={selectedClient} />
        </div>
      )}
    </div>
  );
}
